import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ScheduleCalendar from "../../../components/calendar/ScheduleCalendar";
import ScheduleCalendarActionsHeader from "../../../components/calendar/ScheduleCalendarActionsHeader";
import useCalendarViewDate from "../../../hooks/useCalendarViewDate";
import CalendarScheduleModel from "../../../models/CalendarScheduleModel";
import {
  indexStudentFormatSchedules,
  indexStudentFormatSchedulesDateRange,
} from "../../../api/endpoints/students/schedules";
import moment from "moment";
import NoSchedules from "../../../components/calendar/NoSchedules";
import { makeApiRequest, showApiErrorMessages } from "../../../helpers";
import { showChangeScheduleRequests } from "../../../api/endpoints/students/requests";
import { ApiResponse } from "../../../api/apiResponse";
import RequestsModel from "../../../api/models/RequestsModel";
import LoadingSpinner from "../../../components/LoadingSpinner";
import PendingSchedule from "../../../components/calendar/PendingSchedule";

export default function WeeklyScheduleStudent() {
  const LocalBasePath = "page.tutor.dashboard";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [recitationSchedule, setSchedule] = useState<object>({});
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [requests, setRequests] = useState<RequestsModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getUserSchedule: any = async () => {
    const startOfTheWeek = moment(viewingDate).day(0);
    // The following will generate the 7 days of the week that the viewing date falls in.
    const dates = [...Array(7)].map((_, i) =>
      startOfTheWeek.clone().add(i, "day")
    );
    const UserScheduleApi = await indexStudentFormatSchedulesDateRange({
      filters: {
        from_date: dates[0].format("DD-MM-YYYY"),
        to_date: dates[dates.length - 1].format("DD-MM-YYYY"),
      },
    });
    if (UserScheduleApi.isSuccess) {
      return UserScheduleApi.data;
    }
  };

  useEffect(() => {
    fetchAndSetSchedule();
  }, [viewingDate]);

  const fetchAndSetSchedule = async () => {
    let fetchedSchedule = await getUserSchedule();
    setSchedule(fetchedSchedule);
    showRequests();
  };

  const mergeSchedules = () => {
    let calendarRecitationSchedule = new CalendarScheduleModel(
      recitationSchedule
    );

    calendarRecitationSchedule.slots = calendarRecitationSchedule.slots.filter(
      (slot) => !slot.isCandidateSlot
    );
    calendarRecitationSchedule.modifySlotsStateByDate(viewingDate);

    /**
     * The order of spreading schedules is important as we may have duplication or different slots with the same start and end time
     * The last added slots will override the first slots in the UI if there is duplication
     * So, we give here the higher priority to the recitation schedule
     */
    let calendarSchedule = new CalendarScheduleModel({});
    calendarSchedule.slots = [...calendarRecitationSchedule.slots];
    return calendarSchedule;
  };

  const showRequests = async () => {
    setIsLoading(true);
    await makeApiRequest(
      () =>
        showChangeScheduleRequests({
          filters: { sender_type: "Student", status: "pending" },
        }),
      (response: ApiResponse) => {
        setIsLoading(false);
        if (response.isSuccess) {
          const { data } = response;
          const requestsList: RequestsModel[] = RequestsModel.fromApiResponse(
            data?.result,
            "sender"
          );
          setRequests(requestsList);
        } else {
          showApiErrorMessages(response);
        }
      }
    );
  };

  const scheduleContent = () => {
    const hasSlots = mergeSchedules().slots.length > 0;

    if (isLoading) {
      return (
        <div className={` flex justify-center items-center w-full h-full`}>
          <LoadingSpinner size={32} />
        </div>
      );
    }
    if (hasSlots) {
      return (
        <>
          <ScheduleCalendarActionsHeader
            selectedDate={viewingDate}
            viewingDate={viewingDate}
            onChangeViewingDate={onChangeViewingDate}
          />

          <div className="h-full overflow-auto">
            <ScheduleCalendar
              selectedDate={viewingDate}
              viewingDate={viewingDate}
              schedule={mergeSchedules()}
              compact={true}
              cancel={true}
              onChangeViewingDate={onChangeViewingDate}
            />
          </div>
        </>
      );
    } else {
      const hasPendingRequest = requests.length > 0;
      return hasPendingRequest ? <PendingSchedule /> : <NoSchedules />;
    }
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-4 p-6">
        <h2 className="w-full text-2xl font-semibold text-gray-900">
          {t("schedule.weeklySchedule")}
        </h2>
      </div>

      {scheduleContent()}
    </div>
  );
}
